export type HealthHistoryTable = 'health_metrics' | 'workout_sessions' | 'sleep_logs';
export type HealthHistoryOperation = 'insert' | 'update' | 'delete' | 'sleep_import';
export interface HealthHistoryMutationClient {
  rpc(name: string, args: Record<string, unknown>): PromiseLike<{ data: unknown; error: unknown; status?: number }>;
}
export interface HealthHistoryMutationContext {
  userId: string; connectionId: string; provider: 'apple_health' | 'health_connect'; generation: string; runId: string;
}
export interface HealthHistoryMutation {
  table: HealthHistoryTable; operation: HealthHistoryOperation;
  record: Record<string, unknown> | unknown[];
  expected?: { id: string; metadata?: unknown } | null;
}
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object'
  && !Array.isArray(value) && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);
const nonempty = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;
function json(value: unknown, ancestors = new Set<object>()): boolean {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (!Array.isArray(value) && !object(value) || ancestors.has(value as object)
    || Array.isArray(value) && (Object.keys(value).length !== value.length
      || Object.keys(value).some((key, index) => key !== String(index)))) return false;
  ancestors.add(value as object);
  const valid = Object.values(value).every(item => json(item, ancestors));
  ancestors.delete(value as object);
  return valid;
}
function freeze<T>(value: T): T {
  if (value !== null && typeof value === 'object') {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
  return value;
}
const keys = (value: Record<string, unknown>, expected: string[]) => Object.keys(value).length === expected.length
  && expected.every(key => Object.prototype.hasOwnProperty.call(value, key));

/** The server locks the current connection while it validates and mutates.
 * Client checks remain useful for feedback, but cannot authorize a later write.
 * A rejected or uncertain RPC is never replayed here and never falls back to REST.
 */
export async function mutateHealthHistory(
  client: HealthHistoryMutationClient, context: HealthHistoryMutationContext, mutation: HealthHistoryMutation,
  assertCurrent: () => void | Promise<void> = () => {},
): Promise<{ data: unknown; error: unknown; status?: number }> {
  if (![context.userId, context.connectionId, context.generation, context.runId].every(nonempty)
    || !['apple_health', 'health_connect'].includes(context.provider)
    || !['health_metrics', 'workout_sessions', 'sleep_logs'].includes(mutation.table)
    || !['insert', 'update', 'delete', 'sleep_import'].includes(mutation.operation)
    || !json(mutation.record) || !json(mutation.expected ?? null)) throw new Error('invalid_health_history_mutation');
  const expected = mutation.expected ?? null;
  const sleep = mutation.operation === 'sleep_import';
  if (sleep ? mutation.table !== 'sleep_logs' || !Array.isArray(mutation.record) || expected !== null
    : !object(mutation.record) || mutation.record.user_id !== context.userId || mutation.record.source !== context.provider
      || !nonempty(mutation.record.external_id) || mutation.table === 'sleep_logs' && mutation.operation !== 'delete') throw new Error('invalid_health_history_mutation');
  if (sleep && (mutation.record as unknown[]).some(row => !object(row) || row.user_id !== context.userId || row.source !== context.provider)) throw new Error('invalid_health_history_mutation');
  if (mutation.operation === 'update' ? !object(expected) || !nonempty(expected.id)
    || !keys(expected, mutation.table === 'health_metrics' ? ['id', 'metadata'] : ['id']) : expected !== null) throw new Error('invalid_health_history_mutation');
  if (mutation.operation === 'delete' && !keys(mutation.record as Record<string, unknown>, ['user_id', 'source', 'external_id'])) throw new Error('invalid_health_history_mutation');

  // Capture every field before an asynchronous session check can yield. Freeze
  // the cloned transport payload, leaving the caller's own objects untouched.
  const owner = context.userId;
  const args = freeze(structuredClone({
    p_connection_id: context.connectionId, p_provider: context.provider, p_generation: context.generation,
    p_run_id: context.runId, p_table: mutation.table, p_operation: mutation.operation,
    p_record: mutation.record, p_expected: expected,
  }));
  await assertCurrent();
  let reply: Awaited<ReturnType<HealthHistoryMutationClient['rpc']>>;
  try { reply = await client.rpc('mutate_native_health_history_v1', args); }
  finally { await assertCurrent(); }
  if (!object(reply)) throw new Error('health_history_mutation_not_confirmed');
  // Keep the real error body and HTTP status. Only callers recognizing an
  // authoritative SQL protection error may count it as a preserved conflict.
  if (reply.error) return reply;
  const body = reply.data;
  if (!Number.isInteger(reply.status) || reply.status! < 200 || reply.status! >= 300 || !object(body)
    || !keys(body, ['ok', 'version', 'user_id', 'connection_id', 'provider', 'generation', 'run_id', 'table_name', 'operation', 'data'])
    || body.ok !== true || body.version !== 1 || body.user_id !== owner || body.connection_id !== args.p_connection_id
    || body.provider !== args.p_provider || body.generation !== args.p_generation || body.run_id !== args.p_run_id
    || body.table_name !== args.p_table || body.operation !== args.p_operation || !json(body.data)) throw new Error('health_history_mutation_not_confirmed');
  if (sleep ? !object(body.data) : !Array.isArray(body.data) || body.data.length > 1
    || body.data.some(row => !object(row) || !nonempty(row.id)
      || (args.p_operation === 'delete' ? !keys(row, ['id'])
        : row.user_id !== owner || row.source !== args.p_provider
          || row.external_id !== (args.p_record as Record<string, unknown>).external_id))) throw new Error('health_history_mutation_not_confirmed');
  return { data: body.data, error: null, status: reply.status };
}
